import React from 'react';
import { Navigate, createBrowserRouter, Outlet } from 'react-router-dom';

import App from 'App';

import AuthGuardRoute from 'components/common/AuthGuardRoute';
import MainLayout from 'layouts/MainLayout';
import ErrorLayout from 'layouts/ErrorLayout';
import TenantWarehouseGuard from 'layouts/TenantWarehouseGuard';

import Dashboard from 'components/dashboards/default';

// Login
import Login from 'components/Login/Login';

// My Page
import MyPage from 'components/my-page/MyPage';

// Password Reset
import PasswordReset from 'components/password-reset/PasswordReset';
import ConfirmMail from 'components/password-reset/ConfirmMail';
import PasswordResetConfirm from 'components/password-reset/PasswordResetConfirm';

// Product
import ChannelProducts from 'components/add_product/channel-product/ChannelProducts';
import ManageProduct from 'components/add_product/manage-product/ManageProduct';
import ManageProductDetail from 'components/add_product/manage-product/ManageProductDetail';

// Purchase Order
import PurchaseOrders from 'components/purchase-order/purchase-order/PurchaseOrders';
import PurchaseOrderCreate from 'components/purchase-order/purchase-order/PurchaseOrderCreate';
import PurchaseOrderDetails from 'components/purchase-order/purchase-order/PurchaseOrderDetails';
import Vendors from 'components/purchase-order/vendor/Vendors';
import VendorCreate from 'components/purchase-order/vendor/VendorCreate';
import VendorDetails from 'components/purchase-order/vendor/VendorDetails';
import Grn from 'components/purchase-order/grn/Grn';
import GrnDetails from 'components/purchase-order/grn/GrnDetails';
import Putaway from 'components/purchase-order/put-away/Putaway';
import PutawayDetails from 'components/purchase-order/put-away/PutawayDetails';
import CreatePutaway from 'components/purchase-order/put-away/CreatePutaway';

// Order
import Orders from 'components/add_order/order/Orders';
import OrderDetail from 'components/add_order/order/OrderDetail/index';
import Shipment from 'components/add_order/shipment/Shipment';
import Picklist from 'components/add_order/picklist/Picklist';
import PicklistDetail from 'components/add_order/picklist/PicklistDetail/index';
import CreatePicklist from 'components/add_order/picklist/CreatePicklist';
import ProcessPicklist from 'components/add_order/picklist/ProcessPicklist/ProcessPicklist';
import Manifests from 'components/add_order/manifest/Manifests';
import ManifestDetail from 'components/add_order/manifest/ManifestDetail';

// Inventory
import Inventory from 'components/add_inventory/Inventory';
import InventoryHistory from 'components/add_inventory/inventory-history/InventoryHistory';

// Return Manifest
import ReturnManifest from 'components/add_return/return-manifest/ReturnManifests';
import ReturnManifestDetail from 'components/add_return/return-manifest/ReturnManifestDetail';

// Bulk Action
import BulkAction from 'components/bulk-action/BulkAction';

// GatePass
import GatePassListPage from 'components/gatepass/GatePassListPage';
import GatePassDetailPage from 'components/gatepass/GatePassDetailPage';
import ReceivedListPage from 'components/gatepass/ReceivedListPage';

// Admin
import User from 'components/admin/user/User';
import UserDetail from 'components/admin/user/UserDetail';
import Shelf from 'components/admin/shelf/Shelf';
import ShelfDetail from 'components/admin/shelf/ShelfDetail';
import Warehouse from 'components/admin/warehouse/Warehouse';
import WarehouseDetail from 'components/admin/warehouse/WarehouseDetail';
import Sequence from 'components/admin/sequence/Sequence';
import ShippingProvider from 'components/admin/shipping-provider/ShippingProvider';

// Error
import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';

import paths, { rootPaths } from 'routes/paths';
import Root from 'components/common/Root';
import { UserProvider } from '../providers/UserProvider';
import AdminGuardRoute from '../layouts/AdminGuardRoute';
import { PermissionProvider } from '../permissions/context/PermissionProvider';

const routes = [
  {
    element: (
      <UserProvider>
        <PermissionProvider>
          <App />
        </PermissionProvider>
      </UserProvider>
    ),
    children: [
      { index: true, element: <Root /> },
      { path: paths.login, element: <Login /> },
      { path: paths.passwordReset, element: <PasswordReset /> },
      { path: paths.confirmMail, element: <ConfirmMail /> },
      { path: paths.passwordResetConfirm, element: <PasswordResetConfirm /> },
      {
        path: rootPaths.errorsRoot,
        element: <ErrorLayout />,
        children: [
          { path: paths.error404, element: <Error404 /> },
          { path: paths.error500, element: <Error500 /> },
        ],
      },
      {
        path: '/:tenant/:warehouse',
        element: (
          <AuthGuardRoute
            element={
              <TenantWarehouseGuard>
                <MainLayout />
              </TenantWarehouseGuard>
            }
          />
        ),
        children: [
          { index: true, element: <Dashboard /> },
          { path: paths.myPage, element: <MyPage /> },

          // Products
          {
            path: 'products',
            children: [
              { index: true, element: <ChannelProducts /> },
              { path: 'manage-product', element: <ManageProduct /> },
              {
                path: 'manage-product/:productId',
                element: <ManageProductDetail />,
              },
            ],
          },

          // Purchase Orders
          {
            path: 'purchase-orders',
            children: [
              { index: true, element: <PurchaseOrders /> },
              { path: 'create', element: <PurchaseOrderCreate /> },
              { path: ':purchaseOrderId', element: <PurchaseOrderDetails /> },
              { path: 'grns', element: <Grn /> },
              { path: 'grns/:grnId', element: <GrnDetails /> },
              { path: 'put-away', element: <Putaway /> },
              { path: 'put-away/:putawayId', element: <PutawayDetails /> },
              { path: 'put-away/create', element: <CreatePutaway /> },
            ],
          },

          // Vendors
          {
            path: 'vendors',
            children: [
              { index: true, element: <Vendors /> },
              { path: 'create', element: <VendorCreate /> },
              { path: ':vendorId', element: <VendorDetails /> },
            ],
          },

          // Orders
          {
            path: 'orders',
            children: [
              { index: true, element: <Orders /> },
              { path: ':orderId', element: <OrderDetail /> },
            ],
          },

          // Shipments
          {
            path: 'shipments',
            children: [{ index: true, element: <Shipment /> }],
          },

          // Manifests
          {
            path: 'manifests',
            children: [
              { index: true, element: <Manifests /> },
              { path: ':manifestId', element: <ManifestDetail /> },
            ],
          },

          // Picklists
          {
            path: 'picklists',
            children: [
              { index: true, element: <Picklist /> },
              { path: ':picklistId', element: <PicklistDetail /> },
              { path: 'create', element: <CreatePicklist /> },
              { path: 'process/:picklistId', element: <ProcessPicklist /> },
            ],
          },

          // Inventory
          {
            path: 'inventory',
            children: [
              { index: true, element: <Inventory /> },
              { path: 'history', element: <InventoryHistory /> },
            ],
          },

          // Returns
          {
            path: 'return-manifests',
            children: [
              { index: true, element: <ReturnManifest /> },
              { path: ':returnManifestId', element: <ReturnManifestDetail /> },
            ],
          },

          // Bulk Actions
          { path: 'bulk-action', element: <BulkAction /> },

          // GatePass
          {
            path: 'gate-passes',
            element: <AdminGuardRoute element={<Outlet />} />,
            children: [
              { index: true, element: <GatePassListPage /> },
              { path: 'received-list', element: <ReceivedListPage /> },
              { path: ':gate_pass_id', element: <GatePassDetailPage /> },
            ],
          },

          // Admin
          {
            path: rootPaths.adminRoot,
            element: <AdminGuardRoute element={<Outlet />} />,
            children: [
              { path: 'user', element: <User /> },
              { path: 'user/:userId', element: <UserDetail /> },
              { path: 'shelf', element: <Shelf /> },
              { path: 'shelf/:shelfId', element: <ShelfDetail /> },
              { path: 'warehouse', element: <Warehouse /> },
              {
                path: 'warehouse/detail/:warehouseId',
                element: <WarehouseDetail />,
              },
              { path: 'sequence', element: <Sequence /> },
              { path: 'shipping-provider', element: <ShippingProvider /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to={paths.error404} replace /> },
];

export const router = createBrowserRouter(routes, {
  basename: process.env.PUBLIC_URL,
});

export default routes;
